import { userTypeOptions } from "@src/features/users/edit-user-fields/EditUserType";
import {
  UserDetailsAdminEmployeeFragment,
  useUpdateUserMutation,
} from "@src/features/users/user-details/UserDetails.generated";
import SegmentedControl from "@src/lib/ui/SegmentedControl";
import Text from "@src/lib/ui/Text";
import { Input } from "@src/lib/ui/Input";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { UserType } from "@src/types.generated";
import { FC, useMemo, useState } from "react";
import isEmail from "validator/lib/isEmail";
import UserTypeDescription from "./inviteOrEditUserDialog/UserTypeDescription";
import EmailOutlineIcon from "@src/lib/icons/18px/email-outline";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";
import { RoleMultiSelectField } from "@src/features/roles/ui/RoleMultiSelectField";
import { LocationMultiSelectField } from "@src/features/locations/ui/LocationMultiSelectField";
import { isEmailRequiredForUserType } from "./utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserDetailsAdminEmployeeFragment;
};

const EditUserTypeDialog: FC<Props> = ({ open, onOpenChange, user }) => {
  const { userActionPermissions } = useActionPermissions();
  const availableUserTypes = useMemo(() => {
    if (userActionPermissions?.changeOrgSettings) {
      return userTypeOptions;
    }
    return userTypeOptions.filter((option) => option.value !== UserType.Admin);
  }, [userActionPermissions?.changeOrgSettings]);
  const [updateError, setUpdateError] = useState<string | undefined>(undefined);
  const [selectedUserType, setSelectedUserType] = useState(user.userType);
  const [selectedRoleIds, setSelectedRoleIds] = useState(
    user.roles.map((role) => role.id),
  );
  const [selectedLocationIds, setSelectedLocationIds] = useState(
    user.locations.map((location) => location.id),
  );
  const [selectedEmail, setSelectedEmail] = useState(user.email || "");

  const isEmailRequired = isEmailRequiredForUserType({
    from: user.userType,
    to: selectedUserType,
    hasEmail: !!user.email,
  });

  const onSaveClick = () => {
    if (isEmailRequired && !selectedEmail) {
      setUpdateError("Please enter an email");
      return;
    }
    if (
      selectedUserType !== UserType.Employee &&
      selectedEmail &&
      !isEmail(selectedEmail)
    ) {
      setUpdateError("Please use a valid email");
      return;
    }

    if (
      selectedUserType === UserType.Manager &&
      selectedLocationIds.length === 0
    ) {
      setUpdateError("Please select a location");
      return;
    }

    updateUserMutation({
      variables: {
        id: user.id,
        input: {
          userType: { value: selectedUserType },
          roleIds: { value: selectedRoleIds },
          locationIds: { value: selectedLocationIds },
          ...((selectedEmail || selectedUserType === UserType.Employee) && {
            email: { value: selectedEmail },
          }),
        },
      },
    });
  };

  const [updateUserMutation, { loading: updateLoading }] =
    useUpdateUserMutation({
      onCompleted: (data) => {
        if (data.updateUser.success) {
          setUpdateError(undefined);
          onOpenChange(false);
        } else {
          let errorMessage = "An unexpected error occurred";
          if (data.updateUser.error?.code === "EMAIL_IN_USE") {
            errorMessage = "Sorry another user has this email";
          }
          setUpdateError(errorMessage);
        }
      },
      onError() {
        setUpdateError("An unexpected error occurred");
      },
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[80vh] max-w-[400px] flex-col gap-0 p-0"
        hideCloseButton
      >
        <DialogHeader className="shrink-0 border-b p-5">
          <DialogTitle>Edit user type</DialogTitle>
          <DialogDescription className="sr-only">
            Edit user type and permissions
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <SegmentedControl
            aria-label="User type"
            fill
            options={availableUserTypes}
            value={selectedUserType}
            onValueChange={setSelectedUserType}
          />
          {selectedUserType !== UserType.Admin && (
            <UserTypeDescription userType={selectedUserType} />
          )}
          {selectedUserType === UserType.Admin && (
            <div>
              <div className="relative mt-4">
                <EmailOutlineIcon className="absolute top-[42px] left-2.5 size-4 -translate-y-1/2 text-gray-6" />
                <Input
                  label={isEmailRequired ? "Email*" : "Email"}
                  value={selectedEmail}
                  placeholder="Email"
                  onChange={(e) => setSelectedEmail(e.target.value)}
                  inputClassName="pl-[32px]"
                />
              </div>
              <div className="mt-4">
                <RoleMultiSelectField
                  roleIds={selectedRoleIds}
                  setRoleIds={setSelectedRoleIds}
                  placeholder="Select a Role"
                />
              </div>
            </div>
          )}
          {selectedUserType === UserType.Manager && (
            <div>
              <div className="my-4">
                <LocationMultiSelectField
                  locationIds={selectedLocationIds}
                  setLocationIds={setSelectedLocationIds}
                  label="Locations*"
                  placeholder="Select a Location"
                />
              </div>
              <div className="relative">
                <EmailOutlineIcon className="absolute top-[42px] left-2.5 size-4 -translate-y-1/2 text-gray-6" />
                <Input
                  label={isEmailRequired ? "Email*" : "Email"}
                  value={selectedEmail}
                  placeholder="Email"
                  onChange={(e) => setSelectedEmail(e.target.value)}
                  inputClassName="pl-[32px]"
                />
              </div>
              <div className="mt-4">
                <RoleMultiSelectField
                  roleIds={selectedRoleIds}
                  setRoleIds={setSelectedRoleIds}
                  placeholder="Select a Role"
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="shrink-0 border-t p-5">
          {updateError && (
            <Text
              variant="compact-description"
              className="mr-auto self-center text-red-9"
            >
              {updateError}
            </Text>
          )}
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={updateLoading}
            loading={updateLoading}
            onClick={onSaveClick}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserTypeDialog;
